import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaArrowLeft, FaCheck } from "react-icons/fa";
import { showToast } from "../../utils/customToast";
import { resetUserPassword } from "./authHelper";
import "./ForgotPassword.css";

function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const mobile = location.state?.mobile || "";
  const user = location.state?.user || null;
  const isVerified = location.state?.verified || false;

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Guard: if user lands here without completing OTP verification, send them back
  useEffect(() => {
    if (!mobile || !isVerified) {
      showToast.warn("Please complete OTP verification first.");
      navigate("/forgot-password", { replace: true });
    }
  }, [mobile, isVerified, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setErrorMessage("");

    const p1 = newPassword.trim();
    const p2 = confirmPassword.trim();

    if (!p1 || !p2) {
      setErrorMessage("Both password fields are required.");
      showToast.warn("Please fill in both password fields.");
      return;
    }

    if (p1.length < 4) {
      setErrorMessage("Password must be at least 4 characters long.");
      showToast.warn("Password is too short (min 4 characters).");
      return;
    }

    if (p1 !== p2) {
      setErrorMessage("Passwords do not match. Please re-enter.");
      showToast.error("Passwords do not match!");
      return;
    }

    setLoading(true);

    try {
      // 1. Reset password identically to Profile
      const result = await resetUserPassword({
        mobile,
        newPassword: p1,
        confirmPassword: p2,
        userId: user?.ID || user?.id,
        user,
      });

      if (result && result.success) {
        // Trigger custom snackbar / toast
        showToast.save("Password has been changed successfully!");

        // Redirect to Login screen after a short delay
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 1200);
      } else {
        const msg = result?.message || "Failed to reset password. Please try again.";
        setErrorMessage(msg);
        showToast.error(msg);
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setErrorMessage("An unexpected error occurred while resetting password.");
      showToast.error("Error resetting password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Brand Logo */}
        <div className="auth-logo">
          <img src="/media/response_info.jpg" alt="Response Infoway" />
        </div>

        {/* Step Progress Indicator */}
        <div className="auth-steps-indicator">
          <div className="auth-step-dot completed" title="Step 1: Mobile Number"></div>
          <div className="auth-step-dot completed" title="Step 2: OTP Verification"></div>
          <div className="auth-step-dot active" title="Step 3: Reset Password"></div>
        </div>

        {/* Card Header */}
        <div className="auth-header">
          <h2 className="auth-title">Reset Password</h2>
          <p className="auth-subtitle">
            Set a new, secure password for account{" "}
            <span className="highlight-number">+91 {mobile}</span>.
          </p>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="auth-alert-error">
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Form */}
        <form className="auth-form" onSubmit={handleSubmit}>
          {/* New Password */}
          <div className="auth-form-group">
            <label className="auth-form-label" htmlFor="new-pass">
              New Password <span className="required">*</span>
            </label>
            <div className="auth-input-wrapper">
              <input
                id="new-pass"
                type={showNewPassword ? "text" : "password"}
                className="auth-form-control"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowNewPassword((prev) => !prev)}
                aria-label={showNewPassword ? "Hide password" : "Show password"}
                title={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Confirm New Password */}
          <div className="auth-form-group">
            <label className="auth-form-label" htmlFor="confirm-pass">
              Confirm New Password <span className="required">*</span>
            </label>
            <div className="auth-input-wrapper">
              <input
                id="confirm-pass"
                type={showConfirmPassword ? "text" : "password"}
                className="auth-form-control"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                title={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-btn-primary"
            disabled={loading}
          >
            {loading ? (
              <span className="btn-loading-wrapper">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Updating Password...</span>
              </span>
            ) : (
              <>
                <FaCheck />
                <span>Reset Password & Login</span>
              </>
            )}
          </button>
        </form>

        {/* Footer Back Link */}
        <div className="auth-footer">
          <Link to="/" className="auth-back-link">
            <FaArrowLeft /> Cancel & Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
